import {
  CalendarClock,
  CheckCircle2,
  Clock3,
  Goal,
  MessageSquarePlus,
  Plus,
  UserRound,
} from "lucide-react";
import {
  CreateBase,
  Form,
  required,
  useGetIdentity,
  useGetList,
  useLocaleState,
  useNotify,
  useRecordContext,
  useTranslate,
} from "ra-core";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { SaveButton } from "@/components/admin/form";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { DateInput } from "@/components/admin/date-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";

import { contactOptionText } from "../misc/ContactOption";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "../deals/dealUtils";
import type { Company, CompanyInteraction, Contact, Deal } from "../types";

const interactionTypes = [
  "call",
  "message",
  "email",
  "meeting",
  "presentation_sent",
  "demo_scheduled",
  "demo_done",
  "contract_preparation",
  "invoice_sent",
  "payment_received",
  "implementation",
  "support",
  "feature_presentation",
  "upsell",
] as const;

const getInteractionLabel = (
  type: string,
  translate: ReturnType<typeof useTranslate>,
) =>
  translate(`resources.company_interactions.types.${type}`, {
    _: type.replaceAll("_", " "),
  });

const formatDate = (value: string | null | undefined, locale: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const parseDateOnly = (value: string | null | undefined) => {
  if (!value) return null;
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const emptyToNull = (value: unknown) => (value === "" ? null : value);

const isLostDeal = (deal: Deal) => deal.stage === "lost";

export const CompanySalesOverview = ({ company }: { company: Company }) => {
  const translate = useTranslate();
  const [locale = "ru"] = useLocaleState();
  const { dealStages } = useConfigurationContext();

  const { data: dealsData } = useGetList<Deal>(
    "deals",
    {
      filter: { company_id: company.id },
      pagination: { page: 1, perPage: 25 },
      sort: { field: "updated_at", order: "DESC" },
    },
    { enabled: !!company.id },
  );

  const { data: interactionsData } = useGetList<CompanyInteraction>(
    "company_interactions",
    {
      filter: { company_id: company.id },
      pagination: { page: 1, perPage: 25 },
      sort: { field: "interaction_date", order: "DESC" },
    },
    { enabled: !!company.id },
  );

  const deals = dealsData ?? [];
  const interactions = interactionsData ?? [];
  const activeDeal = deals.find((deal) => !isLostDeal(deal)) ?? deals[0];
  const latestInteraction = interactions[0];
  const nextInteraction = interactions
    .filter(
      (interaction) => interaction.next_step || interaction.next_step_date,
    )
    .sort((a, b) => {
      const aDate = parseDateOnly(a.next_step_date)?.getTime() ?? Infinity;
      const bDate = parseDateOnly(b.next_step_date)?.getTime() ?? Infinity;
      return aDate - bDate;
    })[0];

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextStepDate = parseDateOnly(nextInteraction?.next_step_date);
  const isOverdue = !!nextStepDate && nextStepDate < today;
  const stageLabel = activeDeal
    ? (findDealLabel(dealStages, activeDeal.stage) ?? activeDeal.stage)
    : translate("resources.company_interactions.overview.no_deal", {
        _: "No deal",
      });

  return (
    <div className="mb-4 grid gap-3 md:grid-cols-4">
      <OverviewItem
        icon={<Goal className="h-4 w-4" />}
        label={translate("resources.company_interactions.overview.stage", {
          _: "Stage",
        })}
        value={stageLabel}
        description={activeDeal?.name}
      />
      <OverviewItem
        icon={<MessageSquarePlus className="h-4 w-4" />}
        label={translate("resources.company_interactions.overview.latest", {
          _: "Last interaction",
        })}
        value={
          latestInteraction
            ? getInteractionLabel(latestInteraction.type, translate)
            : translate(
                "resources.company_interactions.overview.no_interactions",
                {
                  _: "No interactions",
                },
              )
        }
        description={
          latestInteraction
            ? formatDate(latestInteraction.interaction_date, locale)
            : undefined
        }
      />
      <OverviewItem
        icon={<Clock3 className="h-4 w-4" />}
        label={translate("resources.company_interactions.overview.next_step", {
          _: "Next step",
        })}
        value={
          nextInteraction?.next_step ||
          translate("resources.company_interactions.overview.no_next_step", {
            _: "No next step",
          })
        }
        description={
          nextInteraction?.next_step_date
            ? formatDate(nextInteraction.next_step_date, locale)
            : undefined
        }
        tone={isOverdue ? "danger" : "default"}
      />
      <OverviewItem
        icon={<CheckCircle2 className="h-4 w-4" />}
        label={translate("resources.company_interactions.overview.status", {
          _: "Status",
        })}
        value={
          isOverdue
            ? translate("resources.company_interactions.overview.overdue", {
                _: "Overdue",
              })
            : translate("resources.company_interactions.overview.on_track", {
                _: "On track",
              })
        }
        description={
          nextInteraction?.next_step
            ? translate(
                "resources.company_interactions.overview.has_next_step",
                {
                  _: "Next action planned",
                },
              )
            : undefined
        }
        tone={isOverdue ? "danger" : "default"}
      />
    </div>
  );
};

const OverviewItem = ({
  icon,
  label,
  value,
  description,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  value: string;
  description?: string;
  tone?: "default" | "danger";
}) => (
  <div
    className={`rounded-md border p-3 ${
      tone === "danger"
        ? "border-destructive/40 bg-destructive/5"
        : "bg-muted/20"
    }`}
  >
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-2 line-clamp-2 text-sm font-medium">{value}</div>
    {description && (
      <div className="mt-1 line-clamp-1 text-xs text-muted-foreground">
        {description}
      </div>
    )}
  </div>
);

export const CompanyInteractions = ({
  company: companyProp,
}: {
  company?: Company;
}) => {
  const contextCompany = useRecordContext<Company>();
  const company = companyProp ?? contextCompany;
  const translate = useTranslate();
  const [locale = "ru"] = useLocaleState();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const { data, isPending, error, refetch } = useGetList<CompanyInteraction>(
    "company_interactions",
    {
      filter: { company_id: company?.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "interaction_date", order: "DESC" },
    },
    { enabled: !!company?.id },
  );

  const interactions = data ?? [];

  if (!company) return null;

  return (
    <div className="flex flex-col gap-4 pt-2">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-medium">
            {translate("resources.company_interactions.title", {
              _: "Interactions",
            })}
          </h3>
          <p className="text-sm text-muted-foreground">
            {translate("resources.company_interactions.subtitle", {
              _: "Calls, presentations, demos, contracts, invoices, support and upsells.",
            })}
          </p>
        </div>
        <Button
          size={isMobile ? "lg" : "sm"}
          className={isMobile ? "w-full" : undefined}
          onClick={() => setOpen(true)}
        >
          <Plus className="h-4 w-4" />
          {translate("resources.company_interactions.action.add_report", {
            _: "Add report",
          })}
        </Button>
      </div>

      {isPending ? (
        <div className="text-sm text-muted-foreground">
          {translate("ra.message.loading")}
        </div>
      ) : error ? (
        <div className="text-sm text-destructive">
          {translate("resources.company_interactions.load_error", {
            _: "Could not load interactions.",
          })}
        </div>
      ) : interactions.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
          {translate("resources.company_interactions.empty", {
            _: "No interactions yet.",
          })}
        </div>
      ) : (
        <div className="flex flex-col">
          {interactions.map((interaction) => (
            <InteractionRow
              key={interaction.id}
              interaction={interaction}
              locale={locale}
            />
          ))}
        </div>
      )}

      <AddInteractionDialog
        company={company}
        open={open}
        onOpenChange={setOpen}
        onCreated={() => refetch()}
      />
    </div>
  );
};

const InteractionRow = ({
  interaction,
  locale,
}: {
  interaction: CompanyInteraction;
  locale: string;
}) => {
  const translate = useTranslate();

  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 border-b py-3 last:border-b-0">
      <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-md bg-muted">
        <MessageSquarePlus className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">
            {getInteractionLabel(interaction.type, translate)}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {formatDate(interaction.interaction_date, locale)}
          </span>
        </div>
        {(interaction.contact_id || interaction.deal_id) && (
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
            {interaction.contact_id && (
              <ReferenceField<CompanyInteraction, Contact>
                record={interaction}
                source="contact_id"
                reference="contacts_summary"
                link={false}
                render={({ referenceRecord }) =>
                  referenceRecord
                    ? translate(
                        "resources.company_interactions.linked.contact",
                        {
                          name: `${referenceRecord.first_name ?? ""} ${
                            referenceRecord.last_name ?? ""
                          }`.trim(),
                          _: `Contact: ${referenceRecord.first_name ?? ""} ${
                            referenceRecord.last_name ?? ""
                          }`.trim(),
                        },
                      )
                    : null
                }
              />
            )}
            {interaction.deal_id && (
              <ReferenceField<CompanyInteraction, Deal>
                record={interaction}
                source="deal_id"
                reference="deals"
                link={false}
                render={({ referenceRecord }) =>
                  referenceRecord
                    ? translate("resources.company_interactions.linked.deal", {
                        name: referenceRecord.name,
                        _: `Deal: ${referenceRecord.name}`,
                      })
                    : null
                }
              />
            )}
          </div>
        )}
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6">
          {interaction.summary}
        </p>
        {interaction.result && (
          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
            {translate("resources.company_interactions.fields.result", {
              _: "Result",
            })}
            : {interaction.result}
          </p>
        )}
        {(interaction.next_step || interaction.next_step_date) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <CalendarClock className="h-4 w-4" />
            {interaction.next_step && <span>{interaction.next_step}</span>}
            {interaction.next_step_date && (
              <span>{formatDate(interaction.next_step_date, locale)}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AddInteractionDialog = ({
  company,
  open,
  onOpenChange,
  onCreated,
}: {
  company: Company;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const isMobile = useIsMobile();
  const typeChoices = useMemo(
    () =>
      interactionTypes.map((type) => ({
        value: type,
        label: getInteractionLabel(type, translate),
      })),
    [translate],
  );

  return (
    <CreateBase
      resource="company_interactions"
      record={{
        company_id: company.id,
        type: "call",
        interaction_date: new Date().toISOString(),
        sales_id: identity?.id,
      }}
      mutationOptions={{
        onSuccess: () => {
          onOpenChange(false);
          onCreated();
          notify("resources.company_interactions.created", {
            type: "info",
            messageArgs: {
              _: "Interaction added",
            },
          });
        },
      }}
    >
      {isMobile ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          <SheetContent
            side="bottom"
            className="h-[92dvh] gap-0 rounded-t-xl px-0 pb-0"
          >
            <Form className="flex min-h-0 flex-1 flex-col">
              <SheetHeader className="pr-12">
                <SheetTitle>
                  {translate(
                    "resources.company_interactions.dialog.report_for",
                    {
                      name: company.name,
                      _: `Report for ${company.name}`,
                    },
                  )}
                </SheetTitle>
                <SheetDescription className="line-clamp-2">
                  {company.name}
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-4 pb-4">
                <InteractionFormFields
                  company={company}
                  typeChoices={typeChoices}
                  mobile
                />
              </div>
              <SheetFooter className="border-t bg-background">
                <SaveButton
                  className="h-11 w-full"
                  label="resources.company_interactions.action.save_report"
                />
              </SheetFooter>
            </Form>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="sm:max-w-xl">
            <Form className="flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>
                  {translate(
                    "resources.company_interactions.dialog.create_for",
                    {
                      name: company.name,
                      _: `Add interaction for ${company.name}`,
                    },
                  )}
                </DialogTitle>
              </DialogHeader>
              <InteractionFormFields
                company={company}
                typeChoices={typeChoices}
              />
              <DialogFooter className="items-center justify-between gap-3">
                <ResponsibleManagerHint />
                <SaveButton />
              </DialogFooter>
            </Form>
          </DialogContent>
        </Dialog>
      )}
    </CreateBase>
  );
};

const InteractionFormFields = ({
  company,
  typeChoices,
  mobile = false,
}: {
  company: Company;
  typeChoices: Array<{ value: string; label: string }>;
  mobile?: boolean;
}) => (
  <div className="flex flex-col gap-4">
    <SelectInput
      source="type"
      choices={typeChoices}
      optionText="label"
      optionValue="value"
      helperText={false}
    />
    <TextInput
      source="summary"
      multiline
      rows={mobile ? 5 : 4}
      validate={required()}
      helperText={false}
    />
    <TextInput source="result" multiline rows={2} helperText={false} />
    <TextInput source="next_step" multiline rows={2} helperText={false} />
    <DateInput source="next_step_date" helperText={false} />
    <DateInput source="interaction_date" helperText={false} />
    <div className="grid gap-4 sm:grid-cols-2">
      <ReferenceInput
        source="contact_id"
        reference="contacts_summary"
        filter={{ company_id: company.id }}
        perPage={25}
      >
        <AutocompleteInput
          label="resources.company_interactions.fields.contact_id"
          optionText={contactOptionText}
          parse={emptyToNull}
          helperText={false}
        />
      </ReferenceInput>
      <ReferenceInput
        source="deal_id"
        reference="deals"
        filter={{ company_id: company.id, "archived_at@is": null }}
        perPage={25}
      >
        <AutocompleteInput
          label="resources.company_interactions.fields.deal_id"
          optionText="name"
          parse={emptyToNull}
          helperText={false}
        />
      </ReferenceInput>
    </div>
  </div>
);

const ResponsibleManagerHint = () => {
  const translate = useTranslate();

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <UserRound className="h-4 w-4" />
      {translate("resources.company_interactions.fields.sales_id", {
        _: "Responsible manager",
      })}
    </div>
  );
};
